import React, { useState, useEffect } from 'react';
import './ShopifySales.css';
import API_BASE_URL from '../config';

const ShopifySales = () => {
  const [salesData, setSalesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('7'); // Default to 7 days

  // Hardcoded Shopify credentials
  const shopifyCredentials = {
    apiKey: '8bf0cca956cef3c303d16dd196e0c798',
    apiSecretKey: 'b29d6e6ac5b77cbed8f345172fab63fe',
    adminApiAccessToken: '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',
    storeUrl: 'ingreendients-83d8.myshopify.com'
  };

  const fetchSalesData = async (days = 7) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/shopify/sales-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...shopifyCredentials,
          days: parseInt(days)
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSalesData(data);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch sales data');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching sales data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchSalesData(dateRange);
  }, []);

  // Fetch data when date range changes
  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    fetchSalesData(newRange);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="shopify-sales">
      <div className="shopify-header">
        <h1>Shopify Sales</h1>
        <p>Sales data from your Shopify store</p>
      </div>

      <div className="shopify-content">
        <div className="controls-section">
          <div className="date-range-selector">
            <label htmlFor="dateRange">Date Range:</label>
            <select
              id="dateRange"
              value={dateRange}
              onChange={(e) => handleDateRangeChange(e.target.value)}
              disabled={loading}
            >
              <option value="7">Last 7 days</option>
              <option value="14">Last 14 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </div>
          
          {loading && (
            <div className="loading-indicator">
              <div className="loading-spinner"></div>
              <span>Loading sales data...</span>
            </div>
          )}
        </div>

        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        {salesData && (
          <div className="sales-data-section">
            <h2>Sales Data Results</h2>
            
            <div className="sales-summary">
              <div className="summary-card">
                <h3>Total Orders</h3>
                <div className="summary-value">{salesData.totalOrders}</div>
              </div>
              <div className="summary-card">
                <h3>Total Revenue</h3>
                <div className="summary-value">{formatCurrency(salesData.totalRevenue)}</div>
              </div>
              <div className="summary-card">
                <h3>Average Order Value</h3>
                <div className="summary-value">{formatCurrency(salesData.averageOrderValue)}</div>
              </div>
              <div className="summary-card">
                <h3>Date Range</h3>
                <div className="summary-value">{salesData.dateRange}</div>
              </div>
            </div>

            <div className="orders-table-container">
              <h3>Recent Orders</h3>
              <div className="table-container">
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Items</th>
                      <th>Total</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesData.orders.map((order) => (
                      <tr key={order.id}>
                        <td>{order.orderNumber}</td>
                        <td>{formatDate(order.createdAt)}</td>
                        <td>{order.customerName}</td>
                        <td>{order.itemCount}</td>
                        <td>{formatCurrency(order.totalPrice)}</td>
                        <td>
                          <span className={`status-badge ${order.financialStatus}`}>
                            {order.financialStatus}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="products-table-container">
              <h3>Top Selling Products</h3>
              <div className="table-container">
                <table className="products-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Quantity Sold</th>
                      <th>Revenue</th>
                      <th>Average Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesData.topProducts.map((product) => (
                      <tr key={product.id}>
                        <td>{product.title}</td>
                        <td>{product.sku || 'N/A'}</td>
                        <td>{product.quantitySold}</td>
                        <td>{formatCurrency(product.revenue)}</td>
                        <td>{formatCurrency(product.averagePrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="future-features">
          <h2>Future Features</h2>
          <div className="features-list">
            <div className="feature-item">
              <h3>Sales Analytics</h3>
              <p>Advanced analytics and reporting on sales performance</p>
            </div>
            <div className="feature-item">
              <h3>Inventory Sync</h3>
              <p>Synchronize inventory levels between Shopify and your system</p>
            </div>
            <div className="feature-item">
              <h3>Customer Insights</h3>
              <p>Analyze customer behavior and purchasing patterns</p>
            </div>
            <div className="feature-item">
              <h3>Automated Reports</h3>
              <p>Schedule and automate sales reports and notifications</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopifySales; 